import { FORM_FIELDS_MAP as CALL_TASK_FIELDS } from 'features/daily-plan/components/forms/CallTask/CallTask.constants';
import { FORM_FIELDS_MAP as MARKET_CANDIDATE_FIELDS } from 'features/daily-plan/components/forms/MarketCandidate/MarketCandidate.constants';
import { FORM_FIELDS_MAP as OTHER_TASK_FIELDS } from 'features/daily-plan/components/forms/OtherTask/OtherTask.constants';
import { FORM_FIELDS_MAP as RECRUIT_JOB_ORDER_FIELDS } from 'features/daily-plan/components/forms/RecruitJobOrder/RecruitJobOrder.constants';
import {
  ENDPOINTS as ENDPOINTS_DAILY_PLAN,
  TABLES_KEYS,
  TASK_TYPES
} from 'features/daily-plan/constants';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { DateFormats, FilterType } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { Groupers, LabelRenderers, OptionRenderers, Selectors } from 'UI/utils/renderers';

const COPIES = strings.dailyPlan;

export const PAGE_KEY = TABLES_KEYS.dailyPlan;

export const TEAM_FILTERS_STORAGE_KEYS = {
  DAILY_PLAN_COACH: 'daily-plan-selected-coach',
  DAILY_PLAN_RECRUITER: 'daily-plan-selected-recruiter'
};

export const COMPLETED_TASKS_STORAGE_KEY = 'daily-plan-show-completed-tasks';

export const FILTERS_DEFINITION = [
  'taskTypes',
  'taskStatuses',
  'callTypes',
  'outcomes',
  'entityTypes',
  'contactStatusIds',
  'companyTypes'
];

export const COLUMNS = [
  {
    name: 'id',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'roleId',
    options: {
      filter: false,
      display: 'excluded'
    }
  },
  {
    name: 'taskStatus',
    label: 'Action Status',
    options: {
      filter: true,
      display: true,
      renderer: 'taskStatus'
    }
  },
  {
    name: 'taskType',
    label: 'Action Type',
    options: {
      filter: true,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 150 }
      })
    }
  },
  {
    name: 'startDate',
    label: 'Date & Time',
    options: {
      filter: true,
      display: true
    }
  },
  {
    name: 'contact',
    label: 'Reach Out to',
    options: {
      sort: true,
      display: true,
      renderer: 'contactLink'
    }
  },
  {
    name: 'entity',
    label: 'Contact Type',
    options: {
      filter: true,
      display: true
    }
  },
  {
    name: 'contactStatus',
    label: 'Contact Status',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'candidateType',
    label: 'Candidate Type',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'companyType',
    label: 'Company Type',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'title',
    label: 'Title',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'company',
    label: 'Company',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'linkBuilder',
        config: {
          route: EntityRoutes.CompanyProfile,
          idKey: 'id',
          textKey: 'name',
          enableInTabRedirect: true
        }
      }
    }
  },

  {
    name: 'callType',
    label: 'Call Type',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'outcome',
    label: 'Outcome',
    options: {
      sort: true,
      display: true
    }
  },
  {
    name: 'lastComment',
    label: 'Latest Comment',
    options: {
      sort: true,
      display: true,
      setCellHeaderProps: () => ({
        style: { minWidth: 400 }
      })
    }
  },
  {
    name: 'createdAt',
    label: 'Created on',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'dateBuilder',
        config: { format: DateFormats.MonthDayYearTime }
      }
    }
  },
  {
    name: 'updatedAt',
    label: 'Last Updated on',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'dateBuilder',
        config: { format: DateFormats.MonthDayYearTime }
      }
    }
  },
  {
    name: 'lastCommentAt',
    label: 'Last Comment on',
    options: {
      sort: true,
      display: true,
      renderer: {
        id: 'dateBuilder',
        config: { format: DateFormats.MonthDayYearTime }
      }
    }
  },
  {
    name: 'createdByName',
    label: 'Created by',
    options: {
      sort: true,
      display: true
    }
  }
];

export const EXTRA_FILTERS = [
  {
    name: 'taskTypes',
    paramName: 'taskTypeIds',
    title: COPIES.filters.taskTypes,
    type: FilterType.Autocomplete,
    url: ENDPOINTS_DAILY_PLAN.taskTypes,
    multiple: true
  },
  {
    name: 'taskStatuses',
    paramName: 'taskStatusIds',
    title: COPIES.filters.taskStatus,
    type: FilterType.Autocomplete,
    url: ENDPOINTS_DAILY_PLAN.taskStatuses,
    multiple: true
  },
  {
    name: 'callTypes',
    paramName: 'callTypeIds',
    title: COPIES.filters.callType,
    type: FilterType.Autocomplete,
    url: ENDPOINTS_DAILY_PLAN.callTypes,
    multiple: true
  },
  {
    name: 'outcomes',
    paramName: 'outcomeIds',
    title: COPIES.filters.outcome,
    type: FilterType.Autocomplete,
    url: ENDPOINTS_DAILY_PLAN.outcomes,
    multiple: true,
    groupBy: Groupers.byCategory,
    renderOption: OptionRenderers.title,
    getOptionSelected: Selectors.byId
  },
  {
    name: 'entityTypes',
    paramName: 'entityTypeIds',
    title: COPIES.filters.contactType,
    type: FilterType.Autocomplete,
    url: `${Endpoints.EntityTypes}?orderBy=title&direction=asc&relatedEntities=dailyPlanTasks`,
    multiple: true
  },
  {
    name: 'contactStatusIds',
    paramName: 'contactStatusIds',
    title: 'Contact Status',
    multiple: true,
    type: FilterType.Autocomplete,
    groupBy: Groupers.byRole,
    url: Endpoints.NameStatus,
    renderOption: OptionRenderers.title,
    getOptionLabel: LabelRenderers.contactStatus,
    getOptionSelected: Selectors.byId
  }
];

export const ORDER_BY_OPTIONS = {
  column: 'startDate',
  direction: 'desc'
};

export const FORM_FIELDS_BY_TASK_MAP = {
  [TASK_TYPES.recruitJobOrder]: RECRUIT_JOB_ORDER_FIELDS,
  [TASK_TYPES.marketCandidate]: MARKET_CANDIDATE_FIELDS,
  [TASK_TYPES.other]: OTHER_TASK_FIELDS,
  [TASK_TYPES.preparationCall]: CALL_TASK_FIELDS,
  [TASK_TYPES.debriefCall]: CALL_TASK_FIELDS,
  [TASK_TYPES.closingCall]: CALL_TASK_FIELDS
};

export const MAX_TASKS_LIMIT =
  parseInt(
    window.GPAC_ENV?.DAILY_PLAN_MULTIPLE_TASKS_MAX_LIMIT ??
      process.env.REACT_APP_DAILY_PLAN_MULTIPLE_TASKS_MAX_LIMIT ??
      '180',
    10
  ) || 180;
